import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import { colors } from "../../../../styles/_variables";
import styles from "./UnifiedAccountsModal.module.scss";
import AccountCard from "../../ui/AccountCard/AccountCard";
import Button from "../../../Button/Button";
import { useAppDispatch } from "../../../../store/hooks";
import { useNavigate } from "react-router-dom";
import { useAllAccountsUnified } from "../../hooks/useAllAccountsUnified";
import Spinner from "../../../Spinner/Spinner";
import { useEffect, useState } from "react";
import { AssessmentAccount } from "../../types/types";
import {
  closeModal,
  updateAction,
} from "../../../../store/slices/unifiedBalanceSlice";
import Alert from "../../../Alert/Alert";
import useScreenSize from "../../../../hooks/useScreenSize";
import AccountCardMobile from "../../ui/AccountCardMobile/AccountCardMobile";
import { PATH_APP } from "../../../../routes/paths";

const UnifiedAccountsModal = () => {
  const dispatch = useAppDispatch();
  const {
    subtitle,
    alertText,
    spinnerContainer,
    modalHeader,
    subtitleMobile,
    modalFooterMobile,
    modalMobileHeader,
  } = styles;
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  const [unifiedAccounts, setUnifiedAccounts] = useState<AssessmentAccount[]>(
    [],
  );

  const {
    assessmentAccounts,
    isLoading: assessmentAccountsLoading,
    isFetching,
  } = useAllAccountsUnified();

  useEffect(() => {
    if (!isMobile) return;
    const closeBtn = document.querySelector(
      '[aria-label="Close"]',
    ) as HTMLElement | null;
    if (!closeBtn) return;
    closeBtn.style.visibility =
      assessmentAccountsLoading || isFetching ? "hidden" : "visible";
  }, [assessmentAccountsLoading, isFetching, isMobile]);

  useEffect(() => {
    if (assessmentAccountsLoading || isFetching) return;

    if (!assessmentAccounts || !Array.isArray(assessmentAccounts)) return;

    const allWithoutUnification = assessmentAccounts.every(
      (acc: AssessmentAccount) =>
        acc.assessment.is_allowed && !acc.assessment.unification_status,
    );

    const allNotAllowed = assessmentAccounts.every(
      (acc: AssessmentAccount) =>
        !acc.assessment.is_allowed && !acc.assessment.unification_status,
    );

    if (allWithoutUnification) {
      dispatch(updateAction("INITIAL"));
    } else if (allNotAllowed) {
      dispatch(updateAction("ACCOUNTS_SELECTOR"));
    } else {
      setUnifiedAccounts(
        assessmentAccounts.filter(
          (acc: AssessmentAccount) => acc.assessment.unification_status,
        ),
      );
    }
  }, [assessmentAccounts, assessmentAccountsLoading, isFetching, dispatch]);

  const hasFailedAccount = assessmentAccounts?.some(
    (acc: AssessmentAccount) => acc.assessment.unification_status === "FAILED",
  );

  const allAccountsUnified =
    assessmentAccounts?.length === unifiedAccounts.length;

  const handleContactSupport = () => {
    dispatch(closeModal());
    navigate(PATH_APP.help);
  };

  return (
    <>
      {(assessmentAccountsLoading || isFetching) && (
        <div className={spinnerContainer}>
          <Spinner />
        </div>
      )}
      {!(assessmentAccountsLoading || isFetching) && (
        <>
          <ModalHeader className={modalMobileHeader}>
            {!isMobile ? (
              <h1
                style={{
                  color: colors.primary[500],
                  fontSize: "1.5rem",
                  fontWeight: 600,
                }}
              >
                Saldo unificado
              </h1>
            ) : (
              <h1 className={modalHeader}>Saldo unificado</h1>
            )}
          </ModalHeader>
          <ModalBody>
            {!isMobile ? (
              <p className={subtitle}>Cuentas comitentes unificadas:</p>
            ) : (
              <p className={subtitleMobile}>Cuentas comitentes unificadas</p>
            )}
            {unifiedAccounts.map((acc: AssessmentAccount) =>
              !isMobile ? (
                <AccountCard
                  key={acc.allaria_account.number}
                  legalName={acc.allaria_account.denomination}
                  accountNumber={acc.allaria_account.number}
                  state={acc.assessment.unification_status}
                  isSelected={false}
                  onClick={() => {}}
                />
              ) : (
                <AccountCardMobile
                  key={acc.allaria_account.number}
                  legalName={acc.allaria_account.denomination}
                  state={acc.assessment.unification_status}
                  cvu={acc.allaria_mas_accounts.cvu}
                  alias={acc.allaria_mas_accounts.alias}
                  isSelected={false}
                  onClick={() => {}}
                />
              ),
            )}

            {allAccountsUnified && (
              <Alert variant="INFO" className={alertText}>
                Todas tus cuentas comitentes se encuentran unificadas.
              </Alert>
            )}
          </ModalBody>
          <ModalFooter className={isMobile ? modalFooterMobile : ""}>
            {!isMobile ? (
              <>
                <Button
                  variant="secondary"
                  text="Volver"
                  onPress={() => dispatch(closeModal())}
                />
                <Button
                  variant="primary"
                  text="Unificar saldos"
                  isDisabled={allAccountsUnified}
                  onPress={() => dispatch(updateAction("ACCOUNTS_SELECTOR"))}
                />
              </>
            ) : (
              <div className={modalFooterMobile}>
                {hasFailedAccount ? (
                  <Button
                    variant="secondary"
                    text="Contactar a soporte"
                    className="w-full mb-2.5"
                    onPress={handleContactSupport}
                  />
                ) : (
                  <Button
                    variant={allAccountsUnified ? "primary" : "secondary"}
                    text="Volver"
                    className={allAccountsUnified ? "w-full" : "w-full mb-2.5"}
                    onPress={() => dispatch(closeModal())}
                  />
                )}
                {!allAccountsUnified && (
                  <Button
                    variant="primary"
                    text="Unificar saldos"
                    className="w-full"
                    onPress={() => dispatch(updateAction("ACCOUNTS_SELECTOR"))}
                  />
                )}
              </div>
            )}
          </ModalFooter>
        </>
      )}
    </>
  );
};

export default UnifiedAccountsModal;
